import { useCallback, useRef, useState } from 'react'
import { lookupBankRouting } from 'services'
import { isValidRoutingNumber, normalizeRoutingNumber } from 'utils/bank'

/** Resolved names live for the session: a routing number never changes the bank it points at. */
const nameCache: Record<string, string> = {}
const inFlight: Record<string, Promise<string>> = {}

async function resolve(routingNumber: string): Promise<string> {
  if (nameCache[routingNumber] !== undefined) return nameCache[routingNumber]
  if (!inFlight[routingNumber])
    inFlight[routingNumber] = lookupBankRouting(routingNumber)
      .then(({ bankName }) => {
        nameCache[routingNumber] = bankName || ''
        return nameCache[routingNumber]
      })
      .catch(() => '')
      .finally(() => {
        delete inFlight[routingNumber]
      })
  return inFlight[routingNumber]
}

/**
 * Auto-fill for the bank-name field that sits beside a routing number.
 *
 * Call `resolveBankName` from the routing field's change handler and write whatever it returns into
 * the bank-name field. It returns `''` when the field should be left alone - the routing number is
 * incomplete, no bank was found, or the user has typed a name of their own that we must not clobber.
 */
export function useBankNameLookup() {
  /** The last name this hook filled in, so re-editing the routing number can replace it. */
  const autoFilled = useRef('')
  const [loadingBankName, setLoadingBankName] = useState(false)

  const lookup = useCallback(async (value: any): Promise<string> => {
    if (!isValidRoutingNumber(value)) return ''
    const routingNumber = normalizeRoutingNumber(value)
    if (nameCache[routingNumber] !== undefined) return nameCache[routingNumber]
    setLoadingBankName(true)
    try {
      return await resolve(routingNumber)
    } finally {
      setLoadingBankName(false)
    }
  }, [])

  /**
   * Declare a name that arrived from somewhere else - a record the screen prefilled itself from, say
   * - as one the directory may replace. Without this a prefilled name is indistinguishable from one
   * the user typed, so the lookup stays silent and a new routing number keeps the old bank's name.
   */
  const markBankNameFilled = useCallback((bankName?: string) => {
    autoFilled.current = `${bankName ?? ''}`.trim()
  }, [])

  const resolveBankName = useCallback(
    async (value: any, currentBankName?: string): Promise<string> => {
      const current = `${currentBankName ?? ''}`.trim()
      // A name the user typed themselves outranks the directory.
      if (current && current !== autoFilled.current) return ''
      const bankName = await lookup(value)
      if (!bankName) return ''
      autoFilled.current = bankName
      return bankName
    },
    [lookup],
  )

  /**
   * The `Record<string, InputType>` form case, which is most of them: call it straight after the
   * routing field's own `setInputs` and it patches the bank-name input once the lookup resolves.
   */
  const fillBankNameInput = useCallback(
    (
      inputs: Record<string, any>,
      routingKey: string,
      bankNameKey: string,
      setInputs: (updater: (current: Record<string, any>) => Record<string, any>) => void,
    ) => {
      resolveBankName(inputs[routingKey]?.value, inputs[bankNameKey]?.value).then((bankName) => {
        if (!bankName) return
        setInputs((current) =>
          current[bankNameKey]
            ? { ...current, [bankNameKey]: { ...current[bankNameKey], value: bankName, error: '' } }
            : current,
        )
      })
    },
    [resolveBankName],
  )

  return { resolveBankName, fillBankNameInput, markBankNameFilled, lookup, loadingBankName }
}
